/**
 * Cart Validation Manager
 * Main orchestrator for all cart validation functionality
 * Provides a clean API for initializing and managing cart validation
 */

class CartValidationManager {
  constructor(config = {}) {
    this.config = {
      debug: config.debug || false,
      autoInit: config.autoInit !== false, // Default to true
      ...config
    };

    this.validator = null;
    this.ui = null;
    this.events = null;
    this.isInitialized = false;
  }

  /**
   * Initialize cart validation system
   */
  async init(customerData = null) {
    if (this.isInitialized) {
      this.log('Cart validation already initialized');
      return this;
    }

    try {
      // Initialize core components
      this.validator = new window.CartValidator(this.config);
      this.ui = new window.CartValidationUI(this.config);
      this.events = new window.CartValidationEvents(this.config);

      // Initialize validator with customer data if provided
      if (customerData) {
        const success = this.validator.init(customerData);
        if (!success) {
          this.log('Validator initialization failed - customer not logged in');
        }
      }

      // Initialize event management
      this.events.init(this.validator, this.ui);

      this.isInitialized = true;
      this.log('Cart validation system initialized successfully');

      // Make instance globally available
      window.cartValidationManager = this;

      return this;
    } catch (error) {
      this.log('Error initializing cart validation system:', error);
      throw error;
    }
  }

  /**
   * Initialize with customer data from Liquid template
   */
  initWithCustomerData(customerData) {
    return this.init(customerData);
  }

  /**
   * Run immediate cart validation
   */
  async validateCart(options = {}) {
    if (!this.isInitialized) {
      this.log('Cannot validate - system not initialized');
      return { success: false, error: 'Not initialized' };
    }

    return await this.events.runValidation(options);
  }

  /**
   * Update customer data (for when customer logs in)
   */
  updateCustomerData(customerData) {
    if (!this.validator) {
      this.log('Cannot update customer data - validator not initialized');
      return false;
    }

    return this.validator.init(customerData);
  }

  /**
   * Enable debug mode
   */
  enableDebug() {
    this.config.debug = true;
    if (this.validator) this.validator.config.debug = true;
    if (this.ui) this.ui.config.debug = true;
    if (this.events) this.events.config.debug = true;
    
    this.log('Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug() {
    this.config.debug = false;
    if (this.validator) this.validator.config.debug = false;
    if (this.ui) this.ui.config.debug = false;
    if (this.events) this.events.config.debug = false;
  }

  /**
   * Get system status
   */
  getStatus() {
    return {
      initialized: this.isInitialized,
      validatorReady: this.validator?.isInitialized || false,
      components: {
        validator: !!this.validator,
        ui: !!this.ui,
        events: !!this.events
      },
      config: this.config
    };
  }

  /**
   * Handle review cart action from checkout validation summary
   */
  handleReviewCart() {
    // Close any validation modals
    const overlays = document.querySelectorAll('.cart-validation-overlay');
    overlays.forEach(overlay => overlay.remove());

    // Check if we're already on the cart page
    const currentPath = window.location.pathname;
    const isCartPage = currentPath.includes('/cart') || currentPath === '/cart';

    if (isCartPage) {
      // If on cart page, reload to show updated cart
      window.location.reload();
    } else {
      // Navigate to cart page
      if (window.Shopify?.routes?.cart) {
        window.location.href = window.Shopify.routes.cart;
      } else {
        // Fallback cart URL
        window.location.href = '/cart';
      }
    }

    this.log('Navigating to cart for review');
  }

  /**
   * Clean up system (remove event listeners, etc.)
   */
  cleanup() {
    if (this.events) {
      this.events.cleanup();
    }

    this.validator = null;
    this.ui = null;
    this.events = null;
    this.isInitialized = false;

    this.log('Cart validation system cleaned up');
  }

  /**
   * Debug logging
   */
  log(message, data = null) {
    if (this.config.debug) {
      console.log(`[CartValidationManager] ${message}`, data || '');
    }
  }
}

// Auto-initialize if dependencies are loaded
function autoInitCartValidation() {
  // Check if all required classes are available
  if (window.CartValidator && window.CartValidationUI && window.CartValidationEvents) {
    // Create global instance
    window.cartValidationManager = new CartValidationManager({
      debug: false // Set to true for debugging
    });

    // Auto-initialize if customer data is available in window
    if (window.cartValidationCustomerData) {
      window.cartValidationManager.initWithCustomerData(window.cartValidationCustomerData);
    } else {
      // Initialize without customer data (will only work for logged-in customers)
      window.cartValidationManager.init();
    }
  }
}

// Try auto-initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitCartValidation);
} else {
  autoInitCartValidation();
}

// Export for manual initialization
window.CartValidationManager = CartValidationManager; 